/**
 * @file contractPdf.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-28
 * @modified 2026-08-28
 *
 * Requests a printable sales contract PDF and opens it in a new tab.
 *
 * One document per row as the pricing tables group them — a contract group, or
 * an ungrouped single — with the terms and conditions on page 2, so printing
 * double-sided gives one sheet per contract with its own terms on the back.
 * A bulk selection produces one PDF with the pages already interleaved in the
 * right order, rather than a file per contract for someone to collate.
 *
 *   await openContractPdf([{ groupId }], { onError: showAlert });
 *   await openStraightsContractPdf(rows, { start, end, onError: showAlert });
 */

import { API_URL } from '../config';

async function requestPdf(path, body, { onError } = {}) {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      // The endpoint answers with JSON on failure and a PDF on success, so the
      // error body is readable rather than a blob of binary.
      let message = 'Failed to generate the contract PDF';
      try {
        const data = await response.json();
        if (data?.error) message = data.error;
      } catch (_) { /* non-JSON error body — keep the default */ }
      onError?.(message, 'error');
      return false;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const opened = window.open(url, '_blank');
    if (!opened) {
      onError?.('Your browser blocked the contract window. Allow pop-ups for this site and try again.', 'warning');
    }
    // Give the new tab time to take the blob before the URL is revoked;
    // revoking immediately leaves a blank tab in Safari and Firefox.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch (error) {
    console.error('Contract PDF error:', error);
    onError?.('Connection error while generating the contract PDF', 'error');
    return false;
  }
}

/** rows: [{ groupId }] and/or [{ contractId }] — compounds, blends, bags. */
export function openContractPdf(rows, options = {}) {
  return requestPdf('/api/pricing/contracts/pdf', { rows, copyLabel: options.copyLabel }, options);
}

/** rows: [{ accountNo, itemCode }] plus the period bounds — straights. */
export function openStraightsContractPdf(rows, options = {}) {
  return requestPdf(
    '/api/pricing/straights-contracts/pdf',
    { rows, start: options.start, end: options.end, copyLabel: options.copyLabel },
    options
  );
}

export default openContractPdf;
